// src/components/CaseQuestions.js

import React from "react";
import { Card } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Trash2, PlusCircle, MinusCircle, X, Plus } from "lucide-react";
import { DifficultyLevel, QuestionType } from "../enums";

export default function CaseQuestions({
    caseQuestions,
    handleAddQuestion,
    handleRemoveQuestion,
    handleQuestionChange,
    handleQuestionBasicInfo,
    removeQuestionBasicInfo,
    handleQuestionAdditionalInfo,
    removeQuestionAdditionalInfo,
    handleAddQuestionKeyPoint,
    handleRemoveQuestionKeyPoint,
    handleQuestionKeyPointChange,
    handleQuestionKeyPointDataInput,
    removeQuestionKeyPointDataPoint,
    handleCalculationKeyPointAdd,
    handleCalculationKeyPointRemove,
}) {
    return (
        <div className="space-y-4 mt-6">
            <div className="flex items-center justify-between">
                <label className="block text-sm font-medium">
                    Case Questions
                </label>
                <Button
                    type="button"
                    onClick={handleAddQuestion}
                    variant="outline"
                    size="sm">
                    <PlusCircle className="w-4 h-4 mr-1" />
                    Add Question
                </Button>
            </div>

            {caseQuestions.map((q, qIndex) => (
                <Card key={q.questionId} className="p-4 space-y-2">
                    <div className="flex items-center justify-between">
                        <h4 className="font-semibold">
                            Question #{q.questionId}
                        </h4>
                        <Button
                            type="button"
                            variant="destructive"
                            size="sm"
                            onClick={() => handleRemoveQuestion(qIndex)}>
                            <Trash2 className="w-4 h-4 mr-1" />
                            Remove
                        </Button>
                    </div>

                    {/* Question Text */}
                    <div>
                        <label className="block text-sm font-medium mb-1">
                            Question
                        </label>
                        <Input
                            value={q.question}
                            onChange={(e) =>
                                handleQuestionChange(
                                    qIndex,
                                    "question",
                                    e.target.value
                                )
                            }
                            placeholder="Enter question prompt"
                        />
                    </div>

                    {/* Question Type */}
                    <div>
                        <label className="block text-sm font-medium mb-1">
                            Question Type
                        </label>
                        <div className="flex flex-wrap gap-2">
                            {Object.values(QuestionType).map((typeOption) => (
                                <Button
                                    key={typeOption}
                                    type="button"
                                    variant={
                                        q.type === typeOption
                                            ? "default"
                                            : "outline"
                                    }
                                    onClick={() =>
                                        handleQuestionChange(
                                            qIndex,
                                            "type",
                                            typeOption
                                        )
                                    }
                                    className="capitalize">
                                    {typeOption}
                                </Button>
                            ))}
                        </div>
                    </div>

                    {/* Difficulty */}
                    <div>
                        <label className="block text-sm font-medium mb-1">
                            Difficulty
                        </label>
                        <div className="flex flex-wrap gap-2">
                            {Object.values(DifficultyLevel).map((diff) => (
                                <Button
                                    key={diff}
                                    type="button"
                                    variant={
                                        q.difficulty === diff
                                            ? "default"
                                            : "outline"
                                    }
                                    onClick={() =>
                                        handleQuestionChange(
                                            qIndex,
                                            "difficulty",
                                            diff
                                        )
                                    }
                                    className="capitalize">
                                    {diff}
                                </Button>
                            ))}
                        </div>
                    </div>

                    {/* Expected Time */}
                    <div>
                        <label className="block text-sm font-medium mb-1">
                            Expected Time (minutes)
                        </label>
                        <Input
                            value={q.expectedTime}
                            onChange={(e) =>
                                handleQuestionChange(
                                    qIndex,
                                    "expectedTime",
                                    e.target.value
                                )
                            }
                            placeholder="5-10"
                        />
                    </div>

                    {/* Basic Information (with removal) */}
                    <div>
                        <label className="block text-sm font-medium mb-1">
                            Basic Information (Press Enter to add)
                        </label>
                        <Input
                            onKeyPress={(e) =>
                                handleQuestionBasicInfo(qIndex, e)
                            }
                            placeholder="Add info and press Enter"
                        />
                        <div className="flex flex-wrap gap-2 mt-2">
                            {[...q.basicInformation].map((infoItem) => (
                                <span
                                    key={infoItem}
                                    className="flex items-center gap-1 bg-gray-100 px-2 py-1 rounded text-sm">
                                    {infoItem}
                                    <X
                                        className="w-3 h-3 cursor-pointer"
                                        onClick={() =>
                                            removeQuestionBasicInfo(
                                                qIndex,
                                                infoItem
                                            )
                                        }
                                    />
                                </span>
                            ))}
                        </div>
                    </div>

                    {/* Additional Information (with removal) */}
                    <div>
                        <label className="block text-sm font-medium mb-1">
                            Additional Information (Press Enter to add)
                        </label>
                        <Input
                            onKeyPress={(e) =>
                                handleQuestionAdditionalInfo(qIndex, e)
                            }
                            placeholder="Add additional info and press Enter"
                        />
                        <div className="flex flex-wrap gap-2 mt-2">
                            {[...q.additionalInformation].map((infoItem) => (
                                <span
                                    key={infoItem}
                                    className="flex items-center gap-1 bg-gray-100 px-2 py-1 rounded text-sm">
                                    {infoItem}
                                    <X
                                        className="w-3 h-3 cursor-pointer"
                                        onClick={() =>
                                            removeQuestionAdditionalInfo(
                                                qIndex,
                                                infoItem
                                            )
                                        }
                                    />
                                </span>
                            ))}
                        </div>
                    </div>

                    {/* Key Points */}
                    <div>
                        <div className="flex items-center justify-between mt-4">
                            <label className="block text-sm font-medium">
                                Question Key Points
                            </label>
                            <Button
                                type="button"
                                onClick={() =>
                                    handleAddQuestionKeyPoint(qIndex)
                                }
                                variant="outline"
                                size="sm">
                                <PlusCircle className="w-4 h-4 mr-1" />
                                Add Key Point
                            </Button>
                        </div>
                        {q.keyPoints.map((kp, kpI) => (
                            <Card key={kp.id} className="p-2 mt-2">
                                <div className="flex items-center justify-between">
                                    <Input
                                        placeholder="Key Point Title"
                                        value={kp.title}
                                        onChange={(e) =>
                                            handleQuestionKeyPointChange(
                                                qIndex,
                                                kpI,
                                                "title",
                                                e.target.value
                                            )
                                        }
                                        className="flex-1 mr-2"
                                    />
                                    <Button
                                        type="button"
                                        variant="destructive"
                                        size="sm"
                                        onClick={() =>
                                            handleRemoveQuestionKeyPoint(
                                                qIndex,
                                                kpI
                                            )
                                        }>
                                        <MinusCircle className="w-4 h-4" />
                                    </Button>
                                </div>

                                {/* Data points */}
                                <div className="mt-2">
                                    <Input
                                        placeholder="Add data point and press Enter"
                                        onKeyPress={(e) =>
                                            handleQuestionKeyPointDataInput(
                                                qIndex,
                                                kpI,
                                                e
                                            )
                                        }
                                    />
                                    <div className="flex flex-wrap gap-1 mt-2">
                                        {kp.dataPoints?.map((dp) => (
                                            <span
                                                key={dp}
                                                className="flex items-center gap-1 bg-gray-100 px-2 py-1 rounded text-sm">
                                                {dp}
                                                <X
                                                    className="w-3 h-3 cursor-pointer"
                                                    onClick={() =>
                                                        removeQuestionKeyPointDataPoint(
                                                            qIndex,
                                                            kpI,
                                                            dp
                                                        )
                                                    }
                                                />
                                            </span>
                                        ))}
                                    </div>
                                </div>
                            </Card>
                        ))}
                    </div>

                    {/* MATH QUESTION extras */}
                    {q.type === QuestionType.MATH && (
                        <div className="border p-2 mt-4 rounded-md bg-gray-50">
                            <label className="block text-sm font-medium mb-1">
                                Calculation Final Answer
                            </label>
                            <Input
                                value={q.calculationFinalAnswer || ""}
                                onChange={(e) =>
                                    handleQuestionChange(
                                        qIndex,
                                        "calculationFinalAnswer",
                                        e.target.value
                                    )
                                }
                                placeholder="Numeric or formulaic answer"
                            />
                            <div className="mt-3 space-y-1">
                                <p className="text-sm font-medium">
                                    Calculation Key Points
                                </p>
                                <Button
                                    type="button"
                                    size="sm"
                                    variant="outline"
                                    onClick={() =>
                                        handleCalculationKeyPointAdd(qIndex)
                                    }>
                                    <Plus className="w-4 h-4 mr-1" />
                                    Add Key/Value
                                </Button>
                            </div>
                            {Object.entries(q.calculationKeyPoints || {}).map(
                                ([k, v]) => (
                                    <div
                                        key={k}
                                        className="flex items-center gap-2 mt-2 border rounded p-2 bg-white">
                                        <div className="text-sm font-medium">
                                            {k}:
                                        </div>
                                        <div className="flex-1 text-sm">
                                            {v}
                                        </div>
                                        <X
                                            className="w-4 h-4 cursor-pointer"
                                            onClick={() =>
                                                handleCalculationKeyPointRemove(
                                                    qIndex,
                                                    k
                                                )
                                            }
                                        />
                                    </div>
                                )
                            )}
                        </div>
                    )}
                </Card>
            ))}
        </div>
    );
}
